/**
 * VocabularyExtensionDialog — small modal that lets an admin name a
 * domain-specific canonical the system vocab doesn't cover (§14.3).
 *
 * Validates locally against the same `^[A-Z][A-Za-z0-9]{1,40}$` regex the
 * server enforces (§21.2), so the user gets immediate feedback. The mutation
 * does the real validation, rate-limit, and audit-log.
 */
import * as React from 'react';
import { X } from '@phosphor-icons/react';
import { useMutation } from 'convex/react';
import { Button } from '@fragments-sdk/ui';
import { api } from '@/convex';
import type { Id } from '@/convex';
import s from './VocabularyExtensionDialog.module.scss';

const CANONICAL_ID_RE = /^[A-Z][A-Za-z0-9]{1,40}$/;

const CATEGORIES = [
  'input',
  'overlay',
  'navigation',
  'display',
  'data',
  'layout',
] as const;

type Category = (typeof CATEGORIES)[number];

export interface VocabularyExtensionDialogProps {
  orgId: Id<'orgs'>;
  onClose: () => void;
  onCreated: (canonicalId: string) => void;
}

export function VocabularyExtensionDialog({
  orgId,
  onClose,
  onCreated,
}: VocabularyExtensionDialogProps) {
  const [canonicalId, setCanonicalId] = React.useState('');
  const [definition, setDefinition] = React.useState('');
  const [category, setCategory] = React.useState<Category>('display');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const addCustomCanonical = useMutation(
    api.canonicalPrimitiveMap.addCustomCanonical,
  );

  React.useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const idValid = CANONICAL_ID_RE.test(canonicalId);
  const definitionValid = definition.trim().length > 0;
  const canSubmit = idValid && definitionValid && !submitting;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await addCustomCanonical({
        orgId,
        canonicalId: canonicalId.trim(),
        definition: definition.trim(),
        category,
      });
      onCreated(canonicalId.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add canonical');
      setSubmitting(false);
    }
  }

  return (
    <div className={s.overlay} onClick={onClose} role="presentation">
      <div
        className={s.panel}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Add custom canonical"
      >
        <header className={s.header}>
          <h2 className={s.title}>Name something else</h2>
          <button
            type="button"
            onClick={onClose}
            className={s.closeBtn}
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </header>

        <p className={s.intro}>
          Add a canonical your design system uses but that isn't in the system
          vocabulary. It will be scoped to your organization.
        </p>

        <div className={s.field}>
          <label htmlFor="vocab-id" className={s.label}>
            Canonical ID
          </label>
          <input
            id="vocab-id"
            type="text"
            className={s.input}
            placeholder="PaymentField"
            value={canonicalId}
            onChange={(e) => setCanonicalId(e.target.value)}
            disabled={submitting}
          />
          <p className={s.hint} data-error={canonicalId !== '' && !idValid}>
            PascalCase, 2–41 characters, letters and digits only.
          </p>
        </div>

        <div className={s.field}>
          <label htmlFor="vocab-def" className={s.label}>
            Definition
          </label>
          <input
            id="vocab-def"
            type="text"
            className={s.input}
            placeholder="One-line description of what this primitive does"
            value={definition}
            onChange={(e) => setDefinition(e.target.value)}
            disabled={submitting}
          />
        </div>

        <div className={s.field}>
          <label htmlFor="vocab-cat" className={s.label}>
            Category
          </label>
          <select
            id="vocab-cat"
            className={s.input}
            value={category}
            onChange={(e) => setCategory(e.target.value as Category)}
            disabled={submitting}
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        {error && <div className={s.error}>{error}</div>}

        <footer className={s.footer}>
          <Button
            size="sm"
            variant="outline"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? 'Adding...' : 'Add and override'}
          </Button>
        </footer>
      </div>
    </div>
  );
}
